/**
 * Body columns table for the Text Notes content config. Each row is one column
 * rendered into the generated note body (read-only field badge, display label,
 * format template, required flag, source BQ columns, optional SQL fallback).
 * Built on the shared CustomTable; "Add New Column" lives in the section header.
 * `render_order` is preserved in data but not shown or edited in the UI.
 */
import { useMemo } from "react";
import { Button } from "risa-oasis-ui_v2";
import CustomTable from "../../../../../components/custom-table/custom-table";
import { TableCellType } from "../../../../../components/custom-table/table";
import { BodyColumnRow } from "./textNotesContentUtils";

const TABLE_NAME = "text-notes-body-columns-table";
const TOGGLE_ID_PREFIX = "body-column-required::";

/**
 * Builds an editable-text CustomTable header definition.
 *
 * @param key - Row data key
 * @param label - Column header label
 * @param order - Column display order
 * @param width - Relative column width
 * @returns Header config for EDITABLE_TEXT_WITH_ICON cells
 */
const editableText = (
  key: string,
  label: string,
  order: number,
  width: number,
) => ({
  key,
  label,
  order,
  width,
  sortable: false,
  subKey: "value",
  type: TableCellType.EDITABLE_TEXT_WITH_ICON,
});

const TABLE_HEADERS = [
  {
    key: "fieldName",
    label: "Field",
    order: 0,
    width: 12,
    sortable: false,
    type: TableCellType.BADGE,
  },
  editableText("displayLabel", "Label", 1, 12),
  editableText("formatTemplate", "Format Template", 2, 26),
  {
    key: "required",
    label: "Required",
    order: 3,
    width: 8,
    sortable: false,
    type: TableCellType.TOGGLE_SWITCH,
  },
  {
    key: "bqColumns",
    label: "BQ columns",
    order: 4,
    width: 22,
    sortable: false,
    type: TableCellType.EDITABLE_TAG_LIST,
  },
  editableText("fallbackSelect", "Fallback SELECT", 5, 14),
  {
    key: "delete",
    label: "",
    order: 6,
    width: 6,
    sortable: false,
    type: TableCellType.DELETE_ICON,
  },
];

interface BodyColumnsTableProps {
  resetKey: number;
  rows: BodyColumnRow[];
  onAdd: () => void;
  onRemove: (id: string) => void;
  onCellChange: (id: string, key: string, value: string) => void;
  onRequiredChange: (id: string, value: boolean) => void;
  onBqColumnsChange: (id: string, values: string[]) => void;
}

/**
 * Editable table of text-note body columns with an Add New Column header action.
 *
 * @param resetKey - Remount key for discard/reset
 * @param rows - Current body column rows
 * @param onAdd - Adds a blank column row
 * @param onRemove - Removes a column by id
 * @param onCellChange - Updates a string cell value
 * @param onRequiredChange - Updates the required toggle
 * @param onBqColumnsChange - Updates BQ column tags
 */
const BodyColumnsTable = ({
  resetKey,
  rows,
  onAdd,
  onRemove,
  onCellChange,
  onRequiredChange,
  onBqColumnsChange,
}: BodyColumnsTableProps) => {
  const tableData = useMemo(
    () =>
      rows.map((row) => ({
        id: row.id,
        fieldName: {
          text: row.fieldName || "—",
          color: "#374151",
          bgColor: "#F3F4F6",
        },
        displayLabel: {
          value: row.displayLabel,
          placeholder: "Label",
          emptyLabel: "—",
        },
        formatTemplate: {
          value: row.formatTemplate,
          placeholder: "{value}",
          emptyLabel: "—",
        },
        required: {
          id: `${TOGGLE_ID_PREFIX}${row.id}`,
          defaultChecked: row.required,
        },
        bqColumns: { values: row.bqColumns, placeholder: "Add column" },
        fallbackSelect: {
          value: row.fallbackSelect,
          placeholder: "SQL fallback",
          emptyLabel: "—",
        },
        delete: row.id,
      })),
    [rows],
  );

  /**
   * Routes CustomTable cell edits to the appropriate parent callback.
   *
   * @param rowData - Edited row (must include id)
   * @param fieldKey - Column key that changed
   * @param newValue - New string or tag-list value
   */
  const handleCellChange = (
    rowData: { id?: string },
    fieldKey: string,
    newValue: string | string[],
  ) => {
    if (!rowData?.id) return;
    if (fieldKey === "bqColumns")
      onBqColumnsChange(rowData.id, newValue as string[]);
    else onCellChange(rowData.id, fieldKey, newValue as string);
  };

  /**
   * Maps the required ToggleSwitch emit back to the row id.
   *
   * @param data - Toggle payload with encoded name and boolean value
   */
  const handleToggleChange = (data: { name: string; value: boolean }) => {
    const id = data.name.startsWith(TOGGLE_ID_PREFIX)
      ? data.name.slice(TOGGLE_ID_PREFIX.length)
      : "";
    if (id) onRequiredChange(id, data.value);
  };

  return (
    <div className="flex w-full min-w-0 flex-col gap-3 rounded-lg border border-primaryGray-16 bg-white p-4">
      <div className="flex items-center justify-between gap-4">
        <div className="text-sm font-semibold text-primaryGray-1">
          Text note body columns
        </div>
        <Button
          buttonType="primary"
          size="medium"
          disabled={false}
          onClick={onAdd}
          children="Add New Column"
        />
      </div>
      <div className="w-full min-w-0 [&_.table-container]:w-full [&_.table]:w-full [&_table]:w-full [&_td]:box-border [&_td]:min-w-0 [&_td]:grow [&_tr]:!w-full">
        <CustomTable
          key={`${TABLE_NAME}-${resetKey}`}
          tableName={TABLE_NAME}
          tableHeaders={TABLE_HEADERS}
          tableData={tableData}
          count={tableData.length}
          totalCount={tableData.length}
          itemsPerPage={50}
          isFetching={false}
          hideSearchBar
          hidePagination
          onCellValueChange={handleCellChange}
          onChangeEmit={handleToggleChange}
          onDeleteIconClick={(rowData: { id?: string }) =>
            rowData?.id && onRemove(rowData.id)
          }
        />
      </div>
    </div>
  );
};

export default BodyColumnsTable;
